import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Film, 
  Youtube, 
  Clock, 
  Sparkles, 
  ShieldCheck, 
  Zap, 
  Plus, 
  Calendar, 
  Settings, 
  LogOut, 
  User, 
  Flame, 
  Layers,
  ChevronRight,
  TrendingUp,
  AlertTriangle,
  Play,
  RefreshCw,
  Radar as RadarIcon
} from "lucide-react";
import { Button } from "./components/ui/button";
import { useAuth } from "./context/AuthContext";
import { AuthModal } from "./components/AuthModal";
import { PayPalModal } from "./components/PayPalModal";
import { PipelineProgressModal } from "./components/PipelineProgressModal";
import { ShortCard } from "./components/ShortCard";
import { GeneratorForm } from "./components/GeneratorForm";
import { YouTubeConfigView } from "./components/YouTubeConfigView";
import { AdminMasterPanel } from "./components/AdminMasterPanel";
import { RadarView } from "./components/RadarView";
import { AutoPilotCard } from "./components/AutoPilotCard";
import { AICreditsModal } from "./components/AICreditsModal";
import { AdsLoader, resetAdsConsent } from "./components/AdsLoader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const { user, loading, logout, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard"); // dashboard, generator, shorts, youtube, scheduler, admin
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isPayPalOpen, setIsPayPalOpen] = useState(false);
  const [isAICreditsOpen, setIsAICreditsOpen] = useState(false);
  const [pipelineState, setPipelineState] = useState({
    isOpen: false,
    step: 1,
    statusMessage: "",
    error: null,
    shortData: null
  });

  const [shorts, setShorts] = useState([]);
  const [youtubeStatus, setYoutubeStatus] = useState(null);
  const [loadingShorts, setLoadingShorts] = useState(false);
  const [resettingQuota, setResettingQuota] = useState(false);

  const handleResetDailyQuota = async () => {
    setResettingQuota(true);
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/youtube/reset-daily-quota`, {}, { headers, withCredentials: true });
      await Promise.all([fetchYoutubeStatus(), refreshUser()]);
    } catch (e) {
      console.error(e);
    } finally {
      setResettingQuota(false);
    }
  };

  const fetchShorts = async () => {
    if (!user) return;
    setLoadingShorts(true);
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/shorts`, { headers, withCredentials: true });
      setShorts(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingShorts(false);
    }
  };

  const fetchYoutubeStatus = async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/youtube/status`, { headers, withCredentials: true });
      setYoutubeStatus(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (user) {
      fetchShorts();
      fetchYoutubeStatus();
    }
  }, [user]);

  // Manejadores del Pipeline Modal
  const handlePipelineStarted = () => {
    setPipelineState({
      isOpen: true,
      step: 1,
      statusMessage: "Escribiendo el guion viral con IA...",
      error: null,
      shortData: null
    });
  };

  const handlePipelineCompleted = (data) => {
    setPipelineState({
      isOpen: true,
      step: 3,
      statusMessage: data?.ai_notice
        ? `Short renderizado. Aviso: ${data.ai_notice}`
        : `Short renderizado en 1080×1920 con éxito${data?.engine_label ? ` (guion: ${data.engine_label})` : ""}.`,
      error: null,
      shortData: data
    });
    fetchShorts();
    fetchYoutubeStatus();
  };

  const handlePipelineError = (errorMsg) => {
    setPipelineState(prev => ({
      ...prev,
      isOpen: true,
      error: errorMsg
    }));
  };

  const hoursRemaining = user?.access_status?.hours_left ?? 48;
  const isMasterUser = user?.role === "admin";
  const isFreePlan = user?.access_status?.plan === "free";
  const isTrial = user?.access_status?.status === "trial_active";
  const freeDaily = user?.free_daily_credits ?? 2;
  const proDaily = user?.pro_daily_credits ?? 10;
  const price = `${(user?.app_price_eur ?? 2.99).toFixed(2).replace(".", ",")}€`;
  const paymentPending = !!user?.payment_pending;

  return (
    <div className="min-h-screen overflow-x-clip bg-[#090A0F] text-slate-100 flex flex-col font-sans selection:bg-red-500 selection:text-white">
      {/* Barra de Notificación de Prueba 48h / PayPal si aplica */}
      {user && !isMasterUser && (
        <div 
          data-testid="trial-alert-banner"
          className={`px-4 py-2.5 text-xs flex flex-col sm:flex-row items-center justify-between border-b gap-2 ${
            isFreePlan
              ? "bg-indigo-950/60 border-indigo-500/30 text-indigo-100"
              : "bg-[#151824] border-white/10 text-amber-300"
          }`}
        >
          <div className="flex flex-col sm:flex-row items-center gap-2 max-w-6xl mx-auto w-full justify-between">
            <span className="flex items-center gap-1.5 font-medium text-center sm:text-left">
              <Clock className="w-4 h-4 shrink-0 text-amber-400" />
              {user.has_paid ? (
                <span>Licencia de por vida activa. ¡Gracias por tu compra!</span>
              ) : paymentPending ? (
                <span>Hemos recibido tu comprobante de PayPal. Tu cuenta se desbloqueará en cuanto confirmemos el pago.</span>
              ) : isFreePlan ? (
                <span><strong>Plan Gratis:</strong> {freeDaily} Shorts al día, con marca de agua. Con la licencia de por vida ({price}): {proDaily} al día, Piloto Automático y sin anuncios.</span>
              ) : (
                <span><strong>Prueba Pro: {hoursRemaining}h restantes</strong> con todo desbloqueado. Después sigues gratis con {freeDaily} Shorts al día.</span>
              )}
            </span>

            {!user.has_paid && !paymentPending && (
              <Button
                data-testid="header-paypal-unlock-btn"
                size="sm"
                onClick={() => setIsPayPalOpen(true)}
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs h-7 px-3 shadow shrink-0"
              >
                {isFreePlan ? `Pasar a Pro por ${price}` : `Licencia de por vida ${price}`}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Nota / Advertencia Obligatoria para Usuarios que Paguen */}
      {user && (
        <div 
          data-testid="ai-credits-legal-disclaimer"
          className="bg-black/60 border-b border-white/5 px-4 py-1.5 text-[11px] text-slate-400"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5">
              <span className="text-amber-400 font-bold uppercase tracking-wider text-[10px] bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
                Aviso Importante
              </span>
              <span>
                Tener la licencia y derechos de uso de la app <strong>no implica</strong> la recarga ilimitada de créditos de la IA: cada usuario recarga o gestiona sus propios créditos de IA de forma independiente según su consumo.
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Header Navegación Principal */}
      <header className="sticky top-0 z-40 bg-[#090A0F]/90 backdrop-blur-md border-b border-white/10 px-4 lg:px-8 py-3.5">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          {/* Logo & Marca */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-red-600 to-rose-500 flex items-center justify-center text-white shadow-lg shadow-red-900/40">
              <Film className="w-5 h-5 fill-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-black tracking-tight text-white title-font">
                  CortoViral
                </span>
                <span className="hidden sm:inline text-[10px] uppercase font-mono font-bold bg-red-600/20 text-red-400 px-1.5 py-0.5 rounded border border-red-500/30">
                  SHORTS IA
                </span>
              </div>
              <p className="text-[11px] text-slate-400 -mt-0.5 hidden sm:block">
                Fábrica Automatizada de YouTube Shorts en Español
              </p>
            </div>
          </div>

          {/* Menú Superior y Perfil (en el móvil solo iconos: el botón de pagar ya está en la barra de arriba) */}
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            {user ? (
              <>
                {/* Cuota Diaria Badge con Botón de Regenerar */}
                <div 
                  data-testid="daily-quota-badge"
                  className="flex items-center gap-2 bg-[#12141D] border border-white/10 px-2.5 py-1.5 rounded-lg text-xs"
                >
                  <span className="text-slate-400 hidden sm:inline">Cuota Diaria:</span>
                  <span className="font-mono font-bold text-emerald-400">
                    {youtubeStatus?.daily_quota?.remaining ?? 5}/5
                  </span>
                  {isMasterUser && (
                    <button
                      data-testid="reset-daily-quota-navbar-btn"
                      onClick={handleResetDailyQuota}
                      disabled={resettingQuota}
                      title="Regenerar cuota diaria de videos a 5/5"
                      className="ml-1 p-1 rounded bg-slate-800 hover:bg-emerald-950/40 text-slate-300 hover:text-emerald-400 border border-white/10 transition-all flex items-center gap-1 text-[11px] font-semibold"
                    >
                      <RefreshCw className={`w-3 h-3 ${resettingQuota ? "animate-spin text-emerald-400" : ""}`} />
                      <span className="hidden md:inline">Regenerar</span>
                    </button>
                  )}
                </div>

                {/* Botón de Recargar Créditos de IA */}
                <Button
                  data-testid="navbar-recharge-ai-btn"
                  size="sm"
                  onClick={() => setIsAICreditsOpen(true)}
                  title="Créditos IA"
                  className="bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs h-8 px-2.5 sm:px-3 shadow-md shadow-rose-900/30 flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5 fill-white" />
                  <span className="hidden sm:inline">Créditos IA</span>
                </Button>

                {/* YouTube Status Dot */}
                <div 
                  data-testid="youtube-token-status-dot"
                  className="flex items-center gap-1.5 bg-[#12141D] border border-white/10 px-3 py-1.5 rounded-lg text-xs"
                >
                  <span className={`w-2 h-2 rounded-full ${youtubeStatus?.token_health?.connected ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`} />
                  <span className="hidden sm:inline text-slate-300">
                    {youtubeStatus?.token_health?.connected ? "Canal Activo" : "Sin Conectar"}
                  </span>
                </div>

                {/* Perfil Usuario & Logout */}
                <div className="flex items-center gap-1.5 sm:gap-2 pl-1.5 sm:pl-2 border-l border-white/10">
                  {/* Botón permanente de pagar para usuarios normales */}
                  {!isMasterUser && !user.has_paid && !paymentPending && (
                    <Button
                      data-testid="navbar-pay-button"
                      size="sm"
                      onClick={() => setIsPayPalOpen(true)}
                      className="hidden sm:flex bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs h-8 px-3 shadow items-center gap-1.5"
                    >
                      <span>Pagar {price}</span>
                    </Button>
                  )}

                  <div className="text-right hidden sm:block">
                    <span className="text-xs font-bold text-white block truncate max-w-[120px]">
                      {user.name || user.email}
                    </span>
                    <span className="text-[10px] font-mono text-red-400 block">
                      {isMasterUser ? "★ Maestro" : "Creador"}
                    </span>
                  </div>
                  <Button
                    data-testid="logout-button"
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    className="text-slate-400 hover:text-white hover:bg-slate-800 p-2 h-8 w-8 rounded-lg"
                    title="Cerrar sesión"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  data-testid="open-login-btn"
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsAuthOpen(true)}
                  className="text-xs px-2.5 sm:px-3"
                >
                  Entrar
                </Button>
                <Button
                  data-testid="open-register-btn"
                  size="sm"
                  onClick={() => setIsAuthOpen(true)}
                  className="bg-red-600 hover:bg-red-500 text-white font-bold text-xs px-2.5 sm:px-3"
                >
                  Empezar gratis
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Pestañas de Navegación */}
        {user && (
          <div className="max-w-7xl mx-auto mt-3 flex items-center gap-2 overflow-x-auto pb-1 text-xs">
            <button
              data-testid="nav-tab-dashboard"
              onClick={() => setActiveTab("dashboard")}
              className={`px-3.5 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all ${
                activeTab === "dashboard"
                  ? "bg-red-600 text-white shadow-md shadow-red-900/30"
                  : "bg-slate-900/50 text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              <Zap className="w-3.5 h-3.5" /> Dashboard
            </button>

            <button
              data-testid="nav-tab-generator"
              onClick={() => setActiveTab("generator")}
              className={`px-3.5 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all ${
                activeTab === "generator"
                  ? "bg-red-600 text-white shadow-md shadow-red-900/30"
                  : "bg-slate-900/50 text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" /> Generador IA
            </button>

            <button
              data-testid="nav-tab-autopilot"
              onClick={() => setActiveTab("dashboard")}
              className="px-2.5 py-1 rounded-md bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1 font-bold animate-pulse"
            >
              <Sparkles className="w-3 h-3 text-amber-400" /> Auto-Piloto IA
            </button>

            <button
              data-testid="nav-tab-shorts"
              onClick={() => setActiveTab("shorts")}
              className={`px-3.5 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all ${
                activeTab === "shorts"
                  ? "bg-red-600 text-white shadow-md shadow-red-900/30"
                  : "bg-slate-900/50 text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              <Film className="w-3.5 h-3.5" /> Mis Shorts ({shorts.length})
            </button>

            <button
              data-testid="nav-tab-youtube"
              onClick={() => setActiveTab("youtube")}
              className={`px-3.5 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all ${
                activeTab === "youtube"
                  ? "bg-red-600 text-white shadow-md shadow-red-900/30"
                  : "bg-slate-900/50 text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              <Youtube className="w-3.5 h-3.5" /> YouTube OAuth & Cuota
            </button>

            {isMasterUser && (
              <button
                data-testid="nav-tab-radar"
                onClick={() => setActiveTab("radar")}
                className={`px-3.5 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap ${
                  activeTab === "radar"
                    ? "bg-amber-500 text-slate-950 font-bold shadow-md shadow-amber-500/20"
                    : "bg-slate-900/50 text-amber-400 hover:bg-slate-800"
                }`}
              >
                <RadarIcon className="w-3.5 h-3.5" /> Radar Viral
              </button>
            )}

            {isMasterUser && (
              <button
                data-testid="nav-tab-admin"
                onClick={() => setActiveTab("admin")}
                className={`px-3.5 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all ${
                  activeTab === "admin"
                    ? "bg-amber-500 text-slate-950 font-bold shadow-md shadow-amber-500/20"
                    : "bg-slate-900/50 text-amber-400 hover:bg-slate-800"
                }`}
              >
                <ShieldCheck className="w-3.5 h-3.5" /> Panel Maestro
              </button>
            )}
          </div>
        )}
      </header>

      {/* Contenido Dinámico */}
      <main className="flex-1 max-w-7xl mx-auto w-full p-4 sm:p-6 lg:p-8 space-y-8">
        {!user ? (
          /* Landing de Bienvenida cuando no está logueado */
          <div className="py-12 text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-600/10 border border-red-500/30 text-red-400 text-xs font-mono uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" /> Fabricación de Shorts en 1080×1920 con IA
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-tight">
              Crea YouTube Shorts Virales en Segundos sin Editar
            </h1>
            <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
              Guion estructurado con IA, narraciones de alta retención, escenas en formato vertical 1080×1920 y publicación directa en tu canal de YouTube.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button
                data-testid="landing-get-started-btn"
                size="lg"
                onClick={() => setIsAuthOpen(true)}
                className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white font-black px-8 py-6 text-base rounded-xl shadow-xl shadow-red-900/40"
              >
                Empezar Gratis (48 h de prueba Pro)
              </Button>
              <Button
                data-testid="landing-login-btn"
                variant="secondary"
                size="lg"
                onClick={() => setIsAuthOpen(true)}
                className="w-full sm:w-auto px-6 py-6 text-base rounded-xl border-white/10"
              >
                Acceso Creadores
              </Button>
            </div>
          </div>
        ) : (
          /* Vistas Autenticadas */
          <>
            {activeTab === "dashboard" && (
              <div className="space-y-8">
                {/* Panel de Piloto Automático con Selección Autónoma de Temas y Canal */}
                <AutoPilotCard
                  user={user}
                  onCycleCompleted={(short) => {
                    fetchShorts();
                    fetchYoutubeStatus();
                  }}
                  onOpenPayPal={() => setIsPayPalOpen(true)}
                />

                {/* Métricas Principales */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-[#12141D] border border-white/10 rounded-2xl p-5 shadow-lg">
                    <span className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-1">
                      Shorts Creados
                    </span>
                    <span className="text-3xl font-mono font-black text-white">
                      {shorts.length}
                    </span>
                    <span className="text-[11px] text-slate-500 block mt-1">
                      Renderizados en 1080×1920
                    </span>
                  </div>

                  <div className="bg-[#12141D] border border-white/10 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs uppercase tracking-wider text-slate-400 font-bold block">
                          Cuota Diaria YouTube
                        </span>
                        {isMasterUser && (
                          <button
                            data-testid="reset-quota-card-btn"
                            onClick={handleResetDailyQuota}
                            disabled={resettingQuota}
                            className="text-[10px] text-emerald-400 hover:text-emerald-300 font-bold flex items-center gap-1 bg-emerald-500/10 hover:bg-emerald-500/20 px-2 py-0.5 rounded border border-emerald-500/30 transition-all"
                          >
                            <RefreshCw className={`w-2.5 h-2.5 ${resettingQuota ? "animate-spin" : ""}`} />
                            Regenerar
                          </button>
                        )}
                      </div>
                      <span className="text-3xl font-mono font-black text-emerald-400">
                        {youtubeStatus?.daily_quota?.remaining ?? 5}
                      </span>
                      <span className="text-[11px] text-slate-500 block mt-1">
                        Disponibles hoy (máx 5/día)
                      </span>
                    </div>
                  </div>

                  <div className="bg-[#12141D] border border-white/10 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs uppercase tracking-wider text-slate-400 font-bold block">
                          Motor IA Activo
                        </span>
                        <button
                          data-testid="recharge-ai-card-btn"
                          onClick={() => setIsAICreditsOpen(true)}
                          className="text-[10px] text-rose-400 hover:text-rose-300 font-bold flex items-center gap-1 bg-rose-500/10 hover:bg-rose-500/20 px-2 py-0.5 rounded border border-rose-500/30 transition-all"
                        >
                          <Sparkles className="w-2.5 h-2.5 fill-rose-400" />
                          Recargar
                        </button>
                      </div>
                      <span className="text-xl font-mono font-black text-rose-400 block mt-1">
                        {user.ai_engine || "IA"}
                      </span>
                      <span className="text-[11px] text-slate-500 block mt-1">
                        Guion Pro + SEO Viral · {user.daily_credits ?? 0} shorts hoy
                      </span>
                    </div>
                  </div>

                  <div className="bg-[#12141D] border border-white/10 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
                    <div>
                      <span className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-1">
                        Estado de Licencia
                      </span>
                      <span className="text-base font-bold text-white block mt-1 truncate">
                        {isMasterUser ? "★ Acceso Maestro" : user.has_paid ? "✓ Licencia Pro" : paymentPending ? "Pago en revisión" : isTrial ? `Prueba Pro (${hoursRemaining}h)` : "Plan Gratis"}
                      </span>
                      <span className="text-[11px] text-slate-500 block mt-1">
                        {isMasterUser ? "Sin restricciones" : user.has_paid ? "Desbloqueado de por vida" : isTrial ? `Luego plan Gratis (${freeDaily}/día)` : `${freeDaily} Shorts/día · Pro por ${price}`}
                      </span>
                    </div>

                    {!isMasterUser && !user.has_paid && !paymentPending && (
                      <Button
                        data-testid="dashboard-card-pay-btn"
                        size="sm"
                        onClick={() => setIsPayPalOpen(true)}
                        className="w-full mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs h-7"
                      >
                        Pagar {price} (PayPal Directo)
                      </Button>
                    )}
                  </div>
                </div>

                {/* Generador Rápido integrado en el Dashboard */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  <div className="lg:col-span-7">
                    <GeneratorForm
                      user={user}
                      onStarted={handlePipelineStarted}
                      onCompleted={handlePipelineCompleted}
                      onError={handlePipelineError}
                      onOpenPayPal={() => setIsPayPalOpen(true)}
                    />
                  </div>

                  {/* Feed de Shorts Recientes o Guía Rápida */}
                  <div className="lg:col-span-5 bg-[#12141D] border border-white/10 rounded-2xl p-6 shadow-xl">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-bold text-white flex items-center gap-2">
                        <Flame className="w-4 h-4 text-red-500" /> Últimos Shorts Creados
                      </h4>
                      <button
                        onClick={() => setActiveTab("shorts")}
                        className="text-xs text-red-400 hover:text-red-300 font-semibold"
                      >
                        Ver todos ({shorts.length}) →
                      </button>
                    </div>

                    {shorts.length === 0 ? (
                      <div className="text-center py-8 text-slate-500 text-xs">
                        Aún no has generado ningún Short. ¡Escribe un tema a la izquierda y pulsa Fabricar!
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {shorts.slice(0, 3).map((s) => (
                          <div
                            key={s.id}
                            className="p-3 bg-[#1A1D2B] border border-white/5 rounded-xl flex items-center justify-between gap-3 hover:border-white/15 transition-all"
                          >
                            <div className="min-w-0">
                              <h5 className="text-xs font-semibold text-white truncate">
                                {s.script?.title || s.topic}
                              </h5>
                              <span className="text-[10px] text-slate-400 font-mono">
                                {s.status === "published" ? "✓ Publicado en YouTube" : "• Listo en 1080×1920"}
                              </span>
                            </div>

                            <a
                              href={`${API}/shorts/${s.id}/video`}
                              download={`short_${s.id.slice(0, 6)}.mp4`}
                              className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-[11px] font-semibold border border-white/10 shrink-0"
                            >
                              Descargar
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "generator" && (
              <div className="max-w-3xl mx-auto">
                <GeneratorForm
                  user={user}
                  onStarted={handlePipelineStarted}
                  onCompleted={handlePipelineCompleted}
                  onError={handlePipelineError}
                  onOpenPayPal={() => setIsPayPalOpen(true)}
                />
              </div>
            )}

            {activeTab === "shorts" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-black text-white">Galería de YouTube Shorts</h3>
                    <p className="text-xs text-slate-400">
                      Videos verticales 1080×1920 listos para previsualizar, descargar o publicar
                    </p>
                  </div>
                  <Button
                    data-testid="refresh-shorts-btn"
                    variant="secondary"
                    size="sm"
                    onClick={fetchShorts}
                    className="text-xs"
                  >
                    Actualizar
                  </Button>
                </div>

                {shorts.length === 0 ? (
                  <div className="bg-[#12141D] border border-white/10 rounded-2xl p-12 text-center text-slate-400">
                    <Film className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                    <h4 className="text-base font-bold text-white mb-1">Sin Shorts en la fábrica</h4>
                    <p className="text-xs text-slate-500 mb-4">Empieza generando tu primer video viral con IA</p>
                    <Button
                      onClick={() => setActiveTab("generator")}
                      className="bg-red-600 hover:bg-red-500 text-white font-bold text-xs"
                    >
                      Generar Short Ahora
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {shorts.map((short) => (
                      <ShortCard
                        key={short.id}
                        short={short}
                        onRefresh={fetchShorts}
                        onOpenPayPal={() => setIsPayPalOpen(true)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "youtube" && (
              <YouTubeConfigView onRefreshStatus={fetchYoutubeStatus} />
            )}

            {activeTab === "radar" && isMasterUser && (
              <RadarView onShortCreated={() => { fetchShorts(); fetchYoutubeStatus(); }} />
            )}

            {activeTab === "admin" && isMasterUser && (
              <AdminMasterPanel />
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#090A0F] py-6 px-4 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>CortoViral © 2026 — Fábrica Automatizada de YouTube Shorts</span>
          <span className="flex items-center gap-3">
            <a href="/privacidad#privacidad" className="hover:text-slate-300 underline underline-offset-2">Privacidad</a>
            <a href="/terminos#terminos" className="hover:text-slate-300 underline underline-offset-2">Condiciones</a>
            <button onClick={resetAdsConsent} className="hover:text-slate-300 underline underline-offset-2">Preferencias de anuncios</button>
          </span>
        </div>
      </footer>

      {/* Anuncios (Monetag) solo para quien no ha pagado y con su consentimiento */}
      {!loading && <AdsLoader user={user} />}

      {/* Modales Globales */}
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />
      <PayPalModal
        isOpen={isPayPalOpen}
        onClose={() => setIsPayPalOpen(false)}
        user={user}
        onUnlockSuccess={() => {
          refreshUser();
          fetchShorts();
        }}
      />
      <PipelineProgressModal
        isOpen={pipelineState.isOpen}
        step={pipelineState.step}
        statusMessage={pipelineState.statusMessage}
        error={pipelineState.error}
        shortData={pipelineState.shortData}
        onClose={() => setPipelineState(prev => ({ ...prev, isOpen: false }))}
      />
      <AICreditsModal
        isOpen={isAICreditsOpen}
        onClose={() => setIsAICreditsOpen(false)}
        user={user}
        onSaved={refreshUser}
      />
    </div>
  );
}

export default App;
